import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axiosInstance from '../config/axiosInstance';
import Snackbar from '../components/Snackbar';
import MenuButton from '../components/MenuButton';
import PendingRepliesList from '../components/PendingRepliesList';
import { PAGE_SIZE, Pagination, SortableHeader, SortDirection } from '../components/TableControls';
import trainingService, { PendingReply } from '../services/trainingService';

interface User {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    role: 'admin' | 'trainer' | 'user';
    isActive: boolean;
    emailVerified: boolean;
    createdAt: string;
}

interface NewUserData {
    firstName: string;
    lastName: string;
    email: string;
    password: string;
}

interface EditUserData {
    firstName: string;
    lastName: string;
    email: string;
    role: 'trainer' | 'user';
}

type SortField = 'name' | 'email' | 'status';

const Users: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { t } = useTranslation();
    const [users, setUsers] = useState<User[]>([]);
    const [unreadCounts, setUnreadCounts] = useState<Record<string, number>>({});
    const [pendingReplies, setPendingReplies] = useState<PendingReply[]>([]);
    const [pendingLoading, setPendingLoading] = useState(true);
    // In the URL so "back" from a training plan returns to the same tab.
    const [searchParams, setSearchParams] = useSearchParams();
    const showPending = searchParams.get('tab') === 'pending';
    const [loading, setLoading] = useState(true);
    const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [isAddUserModalOpen, setIsAddUserModalOpen] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [editingUser, setEditingUser] = useState<User | null>(null);
    const [editUserData, setEditUserData] = useState<EditUserData>({ firstName: '', lastName: '', email: '', role: 'user' });
    const [isResetPasswordModalOpen, setIsResetPasswordModalOpen] = useState(false);
    const [resetPasswordUser, setResetPasswordUser] = useState<User | null>(null);
    const [resetPasswordValue, setResetPasswordValue] = useState('');
    const [showResetPassword, setShowResetPassword] = useState(false);
    const [resetPasswordSubmitting, setResetPasswordSubmitting] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [sortField, setSortField] = useState<SortField | null>(null);
    const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
    const [snackbar, setSnackbar] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

    const [newUserData, setNewUserData] = useState<NewUserData>({
        firstName: '',
        lastName: '',
        email: '',
        password: '',
    });

    useEffect(() => {
        fetchUsers();

        const handleVisible = () => {
            if (document.visibilityState === 'visible') fetchUnreadCounts();
        };
        window.addEventListener('focus', fetchUnreadCounts);
        document.addEventListener('visibilitychange', handleVisible);
        return () => {
            window.removeEventListener('focus', fetchUnreadCounts);
            document.removeEventListener('visibilitychange', handleVisible);
        };
    }, []);

    useEffect(() => {
        fetchUnreadCounts();
    }, [location.key]);

    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm]);

    // Close the row action menu on an outside click or Escape.
    useEffect(() => {
        if (!activeDropdown) return;

        const handlePointerDown = (e: MouseEvent) => {
            const target = e.target as HTMLElement;
            if (!target.closest('[data-user-menu]')) {
                setActiveDropdown(null);
            }
        };
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setActiveDropdown(null);
        };

        document.addEventListener('mousedown', handlePointerDown);
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('mousedown', handlePointerDown);
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [activeDropdown]);

    const fetchUsers = async () => {
        try {
            setLoading(true);
            const response = await axiosInstance.get('/users');
            setUsers(response.data.filter((user: User) => user.role !== 'admin'));
        } catch (error) {
            console.error('Error fetching users:', error);
            setSnackbar({ message: 'Failed to fetch users', type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const fetchUnreadCounts = async () => {
        try {
            const [response, pending] = await Promise.all([
                axiosInstance.get('/training/unread-counts'),
                trainingService.getPendingReplies(),
            ]);
            setUnreadCounts(response.data || {});
            setPendingReplies(pending);
        } catch (error) {
            // Non-critical — silently ignore so the user list still loads
            console.error('Error fetching unread counts:', error);
        } finally {
            setPendingLoading(false);
        }
    };

    const handleToggleStatus = async (user: User) => {
        setActiveDropdown(null);
        try {
            await axiosInstance.patch(`/users/${user.id}/status`);
            fetchUsers();
            setSnackbar({ message: t(user.isActive ? 'users.deactivatedSuccess' : 'users.activatedSuccess'), type: 'success' });
        } catch (error: any) {
            const errorMessage = error.response?.data?.message || error.message || 'Failed to update status.';
            setSnackbar({ message: errorMessage, type: 'error' });
        }
    };

    const handleDeleteUser = async (user: User) => {
        setActiveDropdown(null);
        if (!window.confirm(t('users.deleteConfirm', { name: `${user.firstName} ${user.lastName}` }))) return;
        try {
            await axiosInstance.delete(`/users/${user.id}`);
            fetchUsers();
            setSnackbar({ message: t('users.deletedSuccess'), type: 'success' });
        } catch (error: any) {
            const errorMessage = error.response?.data?.message || error.message || 'Failed to delete user.';
            setSnackbar({ message: errorMessage, type: 'error' });
        }
    };

    const openResetPasswordModal = (user: User) => {
        setResetPasswordUser(user);
        setResetPasswordValue('');
        setShowResetPassword(false);
        setActiveDropdown(null);
        setIsResetPasswordModalOpen(true);
    };

    const handleResetPassword = async () => {
        if (!resetPasswordUser || resetPasswordValue.length < 8) return;
        setResetPasswordSubmitting(true);
        try {
            await axiosInstance.post(`/auth/users/${resetPasswordUser.id}/reset-password`, {
                password: resetPasswordValue,
            });
            setIsResetPasswordModalOpen(false);
            setResetPasswordUser(null);
            setResetPasswordValue('');
            setSnackbar({ message: t('users.resetPasswordSuccess'), type: 'success' });
        } catch (error: any) {
            const errorMessage = error.response?.data?.message || error.message || 'Failed to reset password.';
            setSnackbar({ message: errorMessage, type: 'error' });
        } finally {
            setResetPasswordSubmitting(false);
        }
    };

    const openEditModal = (user: User) => {
        setEditingUser(user);
        setEditUserData({ firstName: user.firstName, lastName: user.lastName, email: user.email, role: user.role === 'trainer' ? 'trainer' : 'user' });
        setActiveDropdown(null);
        setIsEditModalOpen(true);
    };

    const handleEditUser = async () => {
        if (!editingUser) return;
        try {
            await axiosInstance.patch(`/users/${editingUser.id}`, editUserData);
            setIsEditModalOpen(false);
            setEditingUser(null);
            fetchUsers();
            setSnackbar({ message: t('users.updatedSuccess'), type: 'success' });
        } catch (error: any) {
            const errorMessage = error.response?.data?.message || error.message || 'Failed to update user.';
            setSnackbar({ message: errorMessage, type: 'error' });
        }
    };

    const handleAddUser = async () => {
        try {
            const userData = {
                firstName: newUserData.firstName.trim(),
                lastName: newUserData.lastName.trim(),
                email: newUserData.email.trim().toLowerCase(),
                password: newUserData.password,
            };

            await axiosInstance.post('/auth/register', userData);

            setIsAddUserModalOpen(false);
            setNewUserData({
                firstName: '',
                lastName: '',
                email: '',
                password: '',
            });
            fetchUsers(); // Refresh the list
            setSnackbar({
                message: t('users.createdSuccess'),
                type: 'success',
            });
        } catch (error: any) {
            console.error('Error adding user:', error);
            const errorMessage = error.response?.data?.message || error.message || 'Failed to add user. Please try again.';
            setSnackbar({ message: errorMessage, type: 'error' });
        }
    };

    // Cycles asc -> desc -> unsorted (back to the API's newest-first order).
    const handleSort = (field: SortField) => {
        if (sortField !== field) {
            setSortField(field);
            setSortDirection('asc');
        } else if (sortDirection === 'asc') {
            setSortDirection('desc');
        } else {
            setSortField(null);
        }
        setCurrentPage(1);
    };

    // Pending (unverified) is a distinct state from active/inactive in the Status
    // column, so it needs its own rank rather than folding into isActive.
    const statusRank = (user: User) => (!user.emailVerified ? 2 : user.isActive ? 0 : 1);

    const sortValue = (user: User, field: SortField): string | number => {
        switch (field) {
            case 'name':
                // Sort by surname, then first name as a tiebreaker.
                return `${user.lastName} ${user.firstName}`.trim().toLowerCase();
            case 'email':
                return user.email.toLowerCase();
            case 'status':
                return statusRank(user);
        }
    };

    const filteredUsers = users.filter((user) => {
        const matchesSearch =
            user.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
            user.lastName.toLowerCase().includes(searchTerm.toLowerCase()) ||
            user.email.toLowerCase().includes(searchTerm.toLowerCase());

        return matchesSearch;
    });

    const sortedUsers = sortField
        ? [...filteredUsers].sort((a, b) => {
              const aValue = sortValue(a, sortField);
              const bValue = sortValue(b, sortField);
              let result: number;
              if (typeof aValue === 'number' && typeof bValue === 'number') {
                  result = aValue - bValue;
              } else {
                  result = String(aValue).localeCompare(String(bValue));
              }
              return sortDirection === 'asc' ? result : -result;
          })
        : filteredUsers;

    const totalPages = Math.max(1, Math.ceil(sortedUsers.length / PAGE_SIZE));
    // Clamp: deleting the last row of the final page, or narrowing the search,
    // can leave currentPage past the end.
    const safePage = Math.min(currentPage, totalPages);
    const pageStart = (safePage - 1) * PAGE_SIZE;
    const paginatedUsers = sortedUsers.slice(pageStart, pageStart + PAGE_SIZE);

    const getRoleBadgeColor = (role: string) => {
        switch (role) {
            case 'admin':
                return 'bg-cyan-100 text-cyan-800';
            case 'trainer':
                return 'bg-cyan-50 text-cyan-700';
            default:
                return 'bg-sky-50 text-sky-600';
        }
    };

    const getStatusBadgeColor = (isActive: boolean) => {
        return isActive ? 'bg-cyan-50 text-cyan-700' : 'bg-slate-100 text-slate-500';
    };

    return (
        <div className="flex-1 flex flex-col min-w-0 bg-background">
            {snackbar && (
                <Snackbar
                    message={snackbar.message}
                    type={snackbar.type}
                    onClose={() => setSnackbar(null)}
                />
            )}

            {/* Header */}
            <header className="h-24 bg-primary border-b border-cyan-600 flex items-center justify-between px-4 md:px-8 shadow-md z-20 sticky top-0 relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-r from-cyan-500 to-blue-500 opacity-90"></div>
                <div className="absolute -right-20 -top-20 w-64 h-64 bg-white/10 rounded-full blur-3xl"></div>

                <div className="relative z-10 flex items-center gap-3 md:gap-6 w-full justify-between">
                    <div className="flex items-center gap-3 md:gap-6 min-w-0">
                        <MenuButton />
                        <div className="bg-white/10 backdrop-blur-md px-3 md:px-6 py-2 rounded-lg border border-white/20 min-w-0">
                            <h1 className="text-xl font-bold text-white flex items-center gap-3">
                                <i className="fa-solid fa-users-gear"></i>
                                {t('users.pageTitle')}
                            </h1>
                        </div>
                    </div>

                    <div className="flex items-center gap-4">
                        <div className="hidden lg:flex items-center relative">
                            <input
                                type="text"
                                placeholder={t('users.searchPlaceholder')}
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="pl-4 pr-12 py-2.5 bg-white/20 border border-white/30 focus:bg-white focus:text-slate-800 placeholder-white/70 focus:placeholder-slate-400 rounded shadow-inner text-white w-64 transition-all outline-none"
                            />
                            <button className="absolute right-1 top-1 bottom-1 px-3 bg-white text-primary rounded font-medium text-sm hover:bg-slate-50 transition-colors">
                                {t('common.search')}
                            </button>
                        </div>

                        <button
                            onClick={() => setIsAddUserModalOpen(true)}
                            className="flex items-center gap-1.5 md:gap-2 px-2.5 md:px-4 py-2 md:py-2.5 bg-white text-primary rounded font-bold hover:shadow-lg hover:bg-slate-50 transition-all shadow-md border-b-4 border-cyan-200 active:border-b-0 active:translate-y-1"
                        >
                            <i className="fa-solid fa-user-plus text-sm md:text-lg"></i>
                            <span className="text-[11px] md:text-sm">{t('users.addMemberButton')}</span>
                        </button>
                    </div>
                </div>
            </header>

            {/* Mobile search — the header input is lg-only, so without this there
                is no way to search on a phone. */}
            <div className="lg:hidden px-3 pt-3 pb-1 bg-slate-50">
                <div className="relative">
                    <i className="fa-solid fa-magnifying-glass absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm"></i>
                    {/* text-base: anything smaller makes iOS Safari zoom in on focus. */}
                    <input
                        type="text"
                        placeholder={t('users.searchPlaceholder')}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full pl-9 pr-4 py-2.5 text-base bg-white border border-slate-200 rounded-lg text-slate-800 placeholder-slate-400 outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                    />
                </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-4 lg:p-6 relative bg-slate-50">
                <div className="max-w-[1600px] mx-auto h-full flex flex-col">
                    {/* Tabs */}
                    <div role="tablist" className="flex gap-1 mb-4 p-1 bg-white border border-slate-200 rounded-xl shadow-sm self-start">
                        {[
                            { key: 'all', label: t('pendingReplies.tabAll'), icon: 'fa-users', active: !showPending },
                            { key: 'pending', label: t('pendingReplies.tabPending'), icon: 'fa-reply', active: showPending },
                        ].map((tab) => (
                            <button
                                key={tab.key}
                                role="tab"
                                aria-selected={tab.active}
                                onClick={() => setSearchParams(tab.key === 'pending' ? { tab: 'pending' } : {}, { replace: true })}
                                className={`flex items-center gap-2 px-3 md:px-4 py-2 rounded-lg text-sm font-semibold transition-all ${tab.active
                                    ? 'bg-gradient-to-r from-cyan-500 to-blue-500 text-white shadow'
                                    : 'text-slate-500 hover:text-slate-800 hover:bg-slate-50'
                                    }`}
                            >
                                <i className={`fa-solid ${tab.icon}`}></i>
                                {tab.label}
                                {tab.key === 'pending' && pendingReplies.length > 0 && (
                                    <span className="min-w-[20px] h-5 flex items-center justify-center bg-red-500 text-white text-[11px] font-bold rounded-full px-1.5">
                                        {pendingReplies.length > 99 ? '99+' : pendingReplies.length}
                                    </span>
                                )}
                            </button>
                        ))}
                    </div>

                    {showPending ? (
                        <div className="bg-white rounded-xl shadow-sm border border-slate-200">
                            <PendingRepliesList
                                loading={pendingLoading}
                                onReplied={(entryId) => {
                                    setPendingReplies((prev) => prev.filter((r) => r.entryId !== entryId));
                                    fetchUnreadCounts();
                                }}
                                onAthleteAnswered={(userId) => {
                                    setPendingReplies((prev) => prev.filter((r) => r.userId !== userId));
                                    fetchUnreadCounts();
                                }}
                                threads={pendingReplies}
                                search={searchTerm}
                            />
                        </div>
                    ) : (
                    /* Users Table */
                    <div className="bg-white rounded-xl shadow-sm border border-slate-200">
                        {/* Table Header */}
                        <div className="hidden md:grid grid-cols-12 gap-4 px-6 py-3 bg-gradient-to-r from-cyan-500 to-blue-500 border-b border-blue-600 text-xs font-black text-white uppercase tracking-wider">
                            <div className="col-span-1">{t('users.table.sno')}</div>
                            <SortableHeader active={sortField === 'name'} direction={sortDirection} onSort={() => handleSort('name')} label={t('users.table.name')} className="col-span-3" />
                            <SortableHeader active={sortField === 'email'} direction={sortDirection} onSort={() => handleSort('email')} label={t('users.table.email')} className="col-span-3" />
                            <div className="col-span-2">{t('users.table.role')}</div>
                            <SortableHeader active={sortField === 'status'} direction={sortDirection} onSort={() => handleSort('status')} label={t('users.table.status')} className="col-span-2" />
                            <div className="col-span-1">{t('common.actions')}</div>
                        </div>

                        {/* Table Content */}
                        {loading ? (
                            <div className="flex items-center justify-center py-20">
                                <div className="text-slate-400">
                                    <i className="fa-solid fa-spinner fa-spin text-3xl"></i>
                                </div>
                            </div>
                        ) : filteredUsers.length === 0 ? (
                            <div className="py-16 px-6 text-center">
                                <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-cyan-100 text-cyan-700 mb-4">
                                    <i className="fa-solid fa-user-plus text-xl"></i>
                                </div>
                                <h3 className="text-lg font-bold text-slate-800 mb-2">{t('users.emptyHeading')}</h3>
                                <p className="text-sm text-slate-500 mb-5">{t('users.emptyDescription')}</p>
                                <button
                                    onClick={() => setIsAddUserModalOpen(true)}
                                    className="inline-flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 text-white rounded-lg font-bold text-sm transition-all shadow-md"
                                >
                                    <i className="fa-solid fa-user-plus"></i>
                                    {t('users.addUser')}
                                </button>
                            </div>
                        ) : (
                            <div className="divide-y divide-slate-100">
                                {paginatedUsers.map((user, index) => (
                                    <React.Fragment key={user.id}>
                                    {/* Mobile card. A one-column stack of the desktop grid
                                        reads as an unlabeled list, so below md the same data
                                        is laid out as a card instead. The grid below is
                                        untouched and still owns every md+ width. */}
                                    <div className="md:hidden px-3 py-2.5 active:bg-slate-50">
                                        <div className="flex items-center gap-2">
                                            <div className="min-w-0 flex-1">
                                                <div className="flex items-center gap-1.5">
                                                    <span className="text-[11px] font-semibold text-slate-300 flex-shrink-0 tabular-nums">
                                                        {(index + 1).toString().padStart(3, '0')}
                                                    </span>
                                                    <span className="text-sm font-semibold text-slate-800 truncate">
                                                        {user.firstName} {user.lastName}
                                                    </span>
                                                    {unreadCounts[user.id] > 0 && (
                                                        <span
                                                            title={`${unreadCounts[user.id]} unread`}
                                                            className="w-2 h-2 rounded-full bg-red-500 flex-shrink-0 animate-pulse"
                                                        />
                                                    )}
                                                </div>
                                                <div className="flex items-center gap-1.5 mt-0.5">
                                                    <span className="text-xs text-slate-400 truncate">{user.email}</span>
                                                </div>
                                                <div className="flex items-center gap-1.5 mt-1.5">
                                                    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold ${getRoleBadgeColor(user.role)}`}>
                                                        {user.role === 'user' ? t('users.roleMember') : user.role === 'trainer' ? t('users.roleTrainer') : user.role.charAt(0).toUpperCase() + user.role.slice(1)}
                                                    </span>
                                                    {!user.emailVerified ? (
                                                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-amber-100 text-amber-700">
                                                            <i className="fa-solid fa-clock text-[9px]"></i>
                                                            Pending
                                                        </span>
                                                    ) : (
                                                        <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium ${getStatusBadgeColor(user.isActive)}`}>
                                                            {user.isActive ? t('users.statusActive') : t('users.statusInactive')}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="relative flex-shrink-0" data-user-menu>
                                                <button
                                                    onClick={() => setActiveDropdown(activeDropdown === `m-${user.id}` ? null : `m-${user.id}`)}
                                                    aria-label={t('common.edit')}
                                                    className="w-10 h-10 flex items-center justify-center hover:bg-slate-100 rounded-lg transition-colors"
                                                >
                                                    <i className="fa-solid fa-ellipsis-vertical text-slate-600"></i>
                                                </button>
                                                {activeDropdown === `m-${user.id}` && (
                                                    <div className="absolute right-0 top-full bg-white rounded-lg shadow-xl border border-slate-200 py-1 min-w-[180px] mt-1 z-50">
                                                        <button
                                                            onClick={() => openEditModal(user)}
                                                            className="w-full px-4 py-2.5 text-left text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2">
                                                            <i className="fa-solid fa-pen text-slate-500 w-4"></i>{t('common.edit')}
                                                        </button>
                                                        <button
                                                            onClick={() => { setActiveDropdown(null); navigate(`/users/${user.id}/training`); }}
                                                            className="w-full px-4 py-2.5 text-left text-sm text-cyan-700 hover:bg-cyan-50 flex items-center gap-2">
                                                            <i className="fa-solid fa-calendar-plus text-cyan-600 w-4"></i>{t('users.addTrainingExercise')}
                                                        </button>
                                                        <button
                                                            onClick={() => handleToggleStatus(user)}
                                                            className={`w-full px-4 py-2.5 text-left text-sm flex items-center gap-2 ${user.isActive ? 'text-red-600 hover:bg-red-50' : 'text-slate-700 hover:bg-slate-50'}`}>
                                                            <i className={`fa-solid ${user.isActive ? 'fa-ban text-red-500' : 'fa-check text-accent'} w-4`}></i>
                                                            {user.isActive ? t('users.deactivate') : t('users.activate')}
                                                        </button>
                                                        <button
                                                            onClick={() => openResetPasswordModal(user)}
                                                            className="w-full px-4 py-2.5 text-left text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2">
                                                            <i className="fa-solid fa-key text-slate-500 w-4"></i>{t('users.resetPassword')}
                                                        </button>
                                                        <button
                                                            onClick={() => handleDeleteUser(user)}
                                                            className="w-full px-4 py-2.5 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2">
                                                            <i className="fa-solid fa-trash text-red-500 w-4"></i>{t('common.delete')}
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    <div
                                        className="hidden md:grid grid-cols-12 gap-4 px-6 py-4 hover:bg-slate-50 transition-colors"
                                    >
                                        <div className="md:col-span-1 font-semibold text-slate-700">
                                            {(index + 1).toString().padStart(3, '0')}
                                        </div>
                                        <div className="md:col-span-3">
                                            <div className="font-medium text-slate-700 flex items-center gap-2">
                                                {user.firstName} {user.lastName}
                                                {unreadCounts[user.id] > 0 && (
                                                    <span
                                                        title={`${unreadCounts[user.id]} unread comment${unreadCounts[user.id] === 1 ? '' : 's'}`}
                                                        className="inline-block w-2.5 h-2.5 rounded-full bg-red-500 shadow-sm animate-pulse"
                                                    />
                                                )}
                                            </div>
                                        </div>
                                        <div className="md:col-span-3">
                                            <div className="text-slate-700">{user.email}</div>
                                        </div>
                                        <div className="md:col-span-2">
                                            <span
                                                className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold ${getRoleBadgeColor(
                                                    user.role
                                                )}`}
                                            >
                                                {user.role === 'user' ? t('users.roleMember') : user.role === 'trainer' ? t('users.roleTrainer') : user.role.charAt(0).toUpperCase() + user.role.slice(1)}
                                            </span>
                                        </div>
                                        <div className="md:col-span-2">
                                            {!user.emailVerified ? (
                                                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
                                                    <i className="fa-solid fa-clock text-[10px]"></i>
                                                    Pending
                                                </span>
                                            ) : (
                                                <span className={`status-badge inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadgeColor(user.isActive)}`}>
                                                    {user.isActive ? t('users.statusActive') : t('users.statusInactive')}
                                                </span>
                                            )}
                                        </div>
                                        <div className="md:col-span-1 relative" data-user-menu>
                                            <button
                                                onClick={() =>
                                                    setActiveDropdown(activeDropdown === user.id ? null : user.id)
                                                }
                                                className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                                            >
                                                <i className="fa-solid fa-ellipsis-vertical text-slate-600"></i>
                                            </button>
                                            {activeDropdown === user.id && (
                                                <div className="absolute right-0 top-full bg-white rounded-lg shadow-xl border border-slate-200 py-1 min-w-[160px] mt-1 z-50">
                                                    <button
                                                        onClick={() => openEditModal(user)}
                                                        className="w-full px-4 py-2 text-left text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2">
                                                        <i className="fa-solid fa-pen text-slate-500 w-4"></i>{t('common.edit')}
                                                    </button>
                                                    {/* Available regardless of emailVerified: the coach builds the
                                                        plan up front, before the athlete's first sign-in. */}
                                                    <button
                                                        onClick={() => {
                                                            setActiveDropdown(null);
                                                            navigate(`/users/${user.id}/training`);
                                                        }}
                                                        className="w-full px-4 py-2 text-left text-sm text-cyan-700 hover:bg-cyan-50 flex items-center gap-2"
                                                    >
                                                        <i className="fa-solid fa-calendar-plus text-cyan-600 w-4"></i>
                                                        {t('users.addTrainingExercise')}
                                                    </button>
                                                    {user.isActive ? (
                                                        <button
                                                            onClick={() => handleToggleStatus(user)}
                                                            className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2">
                                                            <i className="fa-solid fa-ban text-red-500 w-4"></i>
                                                            {t('users.deactivate')}
                                                        </button>
                                                    ) : (
                                                        <button
                                                            onClick={() => handleToggleStatus(user)}
                                                            className="w-full px-4 py-2 text-left text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2">
                                                            <i className="fa-solid fa-check text-accent w-4"></i>
                                                            {t('users.activate')}
                                                        </button>
                                                    )}
                                                    <button
                                                        onClick={() => openResetPasswordModal(user)}
                                                        className="w-full px-4 py-2 text-left text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2">
                                                        <i className="fa-solid fa-key text-slate-500 w-4"></i>{t('users.resetPassword')}
                                                    </button>
                                                    <button
                                                        onClick={() => handleDeleteUser(user)}
                                                        className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2">
                                                        <i className="fa-solid fa-trash text-red-500 w-4"></i>{t('common.delete')}
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    </React.Fragment>
                                ))}
                            </div>
                        )}

                        {/* Pagination */}
                        {filteredUsers.length > 0 && (
                            <Pagination
                                page={safePage}
                                totalPages={totalPages}
                                shown={paginatedUsers.length}
                                total={filteredUsers.length}
                                onPage={setCurrentPage}
                            />
                        )}
                    </div>
                    )}
                </div>
            </div>

            {/* Reset Password Modal */}
            {isResetPasswordModalOpen && resetPasswordUser && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full border border-slate-200 overflow-hidden flex flex-col max-h-[90dvh]">
                        <div className="bg-gradient-to-r from-cyan-600 to-blue-600 px-4 md:px-6 py-3 md:py-4 flex items-center justify-between flex-shrink-0">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-lg bg-white/20 flex items-center justify-center">
                                    <i className="fa-solid fa-key text-white text-lg"></i>
                                </div>
                                <h3 className="text-xl font-bold text-white">{t('users.resetPasswordTitle')}</h3>
                            </div>
                            <button
                                onClick={() => setIsResetPasswordModalOpen(false)}
                                className="w-8 h-8 rounded-lg hover:bg-white/20 text-white flex items-center justify-center transition-colors"
                            >
                                <i className="fa-solid fa-times"></i>
                            </button>
                        </div>
                        <div className="p-4 md:p-6 space-y-3 md:space-y-4 bg-slate-50 overflow-y-auto">
                            <p className="text-sm text-slate-600">
                                {t('users.resetPasswordFor', { name: `${resetPasswordUser.firstName} ${resetPasswordUser.lastName}` })}
                            </p>
                            <div className="space-y-2">
                                <label className="text-xs font-bold text-cyan-700 uppercase tracking-wide">
                                    {t('users.newPasswordLabel')} <span className="text-cyan-500">*</span>
                                </label>
                                <div className="relative">
                                    <i className="fa-solid fa-lock absolute left-3 top-3.5 text-slate-400"></i>
                                    <input
                                        type={showResetPassword ? 'text' : 'password'}
                                        value={resetPasswordValue}
                                        onChange={(e) => setResetPasswordValue(e.target.value)}
                                        className="w-full pl-10 pr-11 py-3 bg-white border border-cyan-200 rounded-lg text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                                        placeholder={t('users.enterPassword')}
                                        autoComplete="new-password"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowResetPassword((v) => !v)}
                                        aria-label={showResetPassword ? t('users.hidePassword') : t('users.showPassword')}
                                        className="absolute right-3 top-3.5 text-slate-400 hover:text-slate-600"
                                    >
                                        <i className={showResetPassword ? 'fa-solid fa-eye-slash' : 'fa-solid fa-eye'}></i>
                                    </button>
                                </div>
                                <p className="text-xs text-slate-500">{t('users.passwordHint')}</p>
                            </div>
                        </div>
                        <div className="px-4 md:px-6 py-3 md:py-4 bg-white border-t border-slate-200 flex gap-2 md:gap-3 flex-shrink-0">
                            <button
                                onClick={() => setIsResetPasswordModalOpen(false)}
                                className="flex-1 px-4 py-2.5 bg-cyan-50 hover:bg-cyan-100 text-cyan-700 rounded-lg font-medium transition-colors border border-cyan-200"
                            >
                                {t('common.cancel')}
                            </button>
                            <button
                                onClick={handleResetPassword}
                                disabled={resetPasswordValue.length < 8 || resetPasswordSubmitting}
                                className="flex-1 px-4 py-2.5 bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 text-white rounded-lg font-bold transition-all shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <i className="fa-solid fa-key mr-2"></i>
                                {t('users.resetPasswordSubmit')}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Edit User Modal */}
            {isEditModalOpen && editingUser && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full border border-slate-200 overflow-hidden flex flex-col max-h-[90dvh]">
                        <div className="bg-gradient-to-r from-cyan-600 to-blue-600 px-4 md:px-6 py-3 md:py-4 flex items-center justify-between flex-shrink-0">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-lg bg-white/20 flex items-center justify-center">
                                    <i className="fa-solid fa-user-pen text-white text-lg"></i>
                                </div>
                                <h3 className="text-xl font-bold text-white">{t('users.editMember')}</h3>
                            </div>
                            <button
                                onClick={() => setIsEditModalOpen(false)}
                                className="w-8 h-8 rounded-lg hover:bg-white/20 text-white flex items-center justify-center transition-colors"
                            >
                                <i className="fa-solid fa-times"></i>
                            </button>
                        </div>
                        <div className="p-4 md:p-6 space-y-3 md:space-y-4 bg-slate-50 overflow-y-auto">
                            <div className="space-y-2">
                                <label className="text-xs font-bold text-cyan-700 uppercase tracking-wide">{t('users.firstNameLabel')} <span className="text-cyan-500">*</span></label>
                                <div className="relative">
                                    <i className="fa-solid fa-user absolute left-3 top-3.5 text-slate-400"></i>
                                    <input
                                        type="text"
                                        value={editUserData.firstName}
                                        onChange={(e) => setEditUserData({ ...editUserData, firstName: e.target.value })}
                                        className="w-full pl-10 pr-4 py-3 bg-white border border-cyan-200 rounded-lg text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                                    />
                                </div>
                            </div>
                            <div className="space-y-2">
                                <label className="text-xs font-bold text-cyan-700 uppercase tracking-wide">{t('users.lastNameLabel')} <span className="text-cyan-500">*</span></label>
                                <div className="relative">
                                    <i className="fa-regular fa-user absolute left-3 top-3.5 text-slate-400"></i>
                                    <input
                                        type="text"
                                        value={editUserData.lastName}
                                        onChange={(e) => setEditUserData({ ...editUserData, lastName: e.target.value })}
                                        className="w-full pl-10 pr-4 py-3 bg-white border border-cyan-200 rounded-lg text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                                    />
                                </div>
                            </div>
                            <div className="space-y-2">
                                <label className="text-xs font-bold text-cyan-700 uppercase tracking-wide">{t('users.table.email')} <span className="text-cyan-500">*</span></label>
                                <div className="relative">
                                    <i className="fa-solid fa-envelope absolute left-3 top-3.5 text-slate-400"></i>
                                    <input
                                        type="email"
                                        value={editUserData.email}
                                        onChange={(e) => setEditUserData({ ...editUserData, email: e.target.value })}
                                        className="w-full pl-10 pr-4 py-3 bg-white border border-cyan-200 rounded-lg text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                                    />
                                </div>
                            </div>
                            <div className="space-y-2">
                                <label className="text-xs font-bold text-cyan-700 uppercase tracking-wide">{t('users.table.role')}</label>
                                <div className="relative">
                                    <i className="fa-solid fa-tag absolute left-3 top-3.5 text-slate-400"></i>
                                    <select
                                        value={editUserData.role}
                                        onChange={(e) => setEditUserData({ ...editUserData, role: e.target.value as 'trainer' | 'user' })}
                                        className="w-full pl-10 pr-4 py-3 bg-white border border-cyan-200 rounded-lg text-slate-800 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                                    >
                                        <option value="user">{t('users.roleMember')}</option>
                                        <option value="trainer">{t('users.roleTrainer')}</option>
                                    </select>
                                </div>
                            </div>
                        </div>
                        <div className="px-4 md:px-6 py-3 md:py-4 bg-white border-t border-slate-200 flex gap-2 md:gap-3 flex-shrink-0">
                            <button
                                onClick={() => setIsEditModalOpen(false)}
                                className="flex-1 px-4 py-2.5 bg-cyan-50 hover:bg-cyan-100 text-cyan-700 rounded-lg font-medium transition-colors border border-cyan-200"
                            >
                                {t('common.cancel')}
                            </button>
                            <button
                                onClick={handleEditUser}
                                disabled={!editUserData.firstName || !editUserData.lastName || !editUserData.email}
                                className="flex-1 px-4 py-2.5 bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 text-white rounded-lg font-bold transition-all shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <i className="fa-solid fa-save mr-2"></i>
                                {t('users.saveChanges')}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Add User Modal */}
            {isAddUserModalOpen && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full border border-slate-200 overflow-hidden flex flex-col max-h-[90dvh]">
                        {/* Modal Header */}
                        <div className="bg-gradient-to-r from-cyan-600 to-blue-600 px-4 md:px-6 py-3 md:py-4 flex items-center justify-between flex-shrink-0">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-lg bg-white/20 flex items-center justify-center">
                                    <i className="fa-solid fa-user-plus text-white text-lg"></i>
                                </div>
                                <h3 className="text-xl font-bold text-white">{t('users.addNewMember')}</h3>
                            </div>
                            <button
                                onClick={() => setIsAddUserModalOpen(false)}
                                className="w-8 h-8 rounded-lg hover:bg-white/20 text-white flex items-center justify-center transition-colors"
                            >
                                <i className="fa-solid fa-times"></i>
                            </button>
                        </div>

                        {/* Modal Body */}
                        <div className="p-4 md:p-6 space-y-3 md:space-y-4 bg-slate-50 overflow-y-auto">
                            {/* First Name */}
                            <div className="space-y-2">
                                <label className="text-xs font-bold text-cyan-700 uppercase tracking-wide">
                                    {t('users.firstNameLabel')} <span className="text-cyan-500">*</span>
                                </label>
                                <div className="relative">
                                    <i className="fa-solid fa-user absolute left-3 top-3.5 text-slate-400"></i>
                                    <input
                                        type="text"
                                        value={newUserData.firstName}
                                        onChange={(e) =>
                                            setNewUserData({ ...newUserData, firstName: e.target.value })
                                        }
                                        className="w-full pl-10 pr-4 py-3 bg-white border border-cyan-200 rounded-lg text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                                        placeholder={t('users.enterFirstName')}
                                    />
                                </div>
                            </div>

                            {/* Last Name */}
                            <div className="space-y-2">
                                <label className="text-xs font-bold text-cyan-700 uppercase tracking-wide">
                                    {t('users.lastNameLabel')} <span className="text-cyan-500">*</span>
                                </label>
                                <div className="relative">
                                    <i className="fa-regular fa-user absolute left-3 top-3.5 text-slate-400"></i>
                                    <input
                                        type="text"
                                        value={newUserData.lastName}
                                        onChange={(e) =>
                                            setNewUserData({ ...newUserData, lastName: e.target.value })
                                        }
                                        className="w-full pl-10 pr-4 py-3 bg-white border border-cyan-200 rounded-lg text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                                        placeholder={t('users.enterLastName')}
                                    />
                                </div>
                            </div>

                            {/* Email */}
                            <div className="space-y-2">
                                <label className="text-xs font-bold text-cyan-700 uppercase tracking-wide">
                                    {t('users.table.email')} <span className="text-cyan-500">*</span>
                                </label>
                                <div className="relative">
                                    <i className="fa-solid fa-envelope absolute left-3 top-3.5 text-slate-400"></i>
                                    <input
                                        type="email"
                                        value={newUserData.email}
                                        onChange={(e) => setNewUserData({ ...newUserData, email: e.target.value })}
                                        className="w-full pl-10 pr-4 py-3 bg-white border border-cyan-200 rounded-lg text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                                        placeholder="user@example.com"
                                    />
                                </div>
                            </div>

                            {/* Password — set by the admin; the athlete signs in with it */}
                            <div className="space-y-2">
                                <label className="text-xs font-bold text-cyan-700 uppercase tracking-wide">
                                    {t('login.password')} <span className="text-cyan-500">*</span>
                                </label>
                                <div className="relative">
                                    <i className="fa-solid fa-lock absolute left-3 top-3.5 text-slate-400"></i>
                                    <input
                                        type="password"
                                        value={newUserData.password}
                                        onChange={(e) => setNewUserData({ ...newUserData, password: e.target.value })}
                                        className="w-full pl-10 pr-4 py-3 bg-white border border-cyan-200 rounded-lg text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                                        placeholder={t('users.enterPassword')}
                                        autoComplete="new-password"
                                    />
                                </div>
                                <p className="text-xs text-slate-500">{t('users.passwordHint')}</p>
                            </div>
                        </div>

                        {/* Modal Footer */}
                        <div className="px-4 md:px-6 py-3 md:py-4 bg-white border-t border-slate-200 flex gap-2 md:gap-3 flex-shrink-0">
                            <button
                                onClick={() => setIsAddUserModalOpen(false)}
                                className="flex-1 px-4 py-2.5 bg-cyan-50 hover:bg-cyan-100 text-cyan-700 rounded-lg font-medium transition-colors border border-cyan-200"
                            >
                                {t('common.cancel')}
                            </button>
                            <button
                                onClick={handleAddUser}
                                disabled={
                                    !newUserData.firstName ||
                                    !newUserData.lastName ||
                                    !newUserData.email ||
                                    newUserData.password.length < 8
                                }
                                className="flex-1 px-4 py-2.5 bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 text-white rounded-lg font-bold transition-all shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <i className="fa-solid fa-user-plus mr-2"></i>
                                {t('users.addMemberSubmit')}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Users;
